import { useStyletron } from 'baseui';
import { LabelSmall } from 'baseui/typography';

import { AxisBottom, AxisLeft, AxisRight } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { GridColumns, GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { LegendOrdinal } from '@visx/legend';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { useTooltip } from '@visx/tooltip';

import { IObjProps } from 'shared/consts/types';
import { borderRadius } from 'shared/helpers';
import useResponsiveSize from 'shared/hooks/useResponsiveSize';

import { IDualAxisLineGraphProps, IDualAxisLinePathProps } from './ENGTGraphProps';

const toRem = (value: number) => `${value / 16}rem`;

const resolveAxisMax = (data: Array<IObjProps>, paths: Array<IDualAxisLinePathProps>) => {
	if (!paths.length) {
		return 1;
	}

	const values = paths.reduce(
		(result: Array<number>, path) => [...result, ...data.map((datum) => path.yAccessor(datum) || 0)],
		[]
	);
	const maxValue = Math.max(...values, 0);

	return maxValue || 1;
};

const ENGTDualAxisLineChart = ({ width, height, data, paths, xAccessor, ...additionals }: IDualAxisLineGraphProps) => {
	const [css, theme] = useStyletron();
	const { isSmallDesktopScreen } = useResponsiveSize();

	const {
		radius = 8,
		background,
		description,
		leftNumTicks = 5,
		rightNumTicks = 5,
		axisBottomLeft,
		xFormatter = (value) => value,
		leftYFormatter = (value) => value,
		rightYFormatter = (value) => value,
		gridStroke = theme.colors.accent100,
		margin = { top: 80, right: 140, bottom: 30, left: 110 },
		leftYAxisLabel,
		rightYAxisLabel,
		tooltipRequired = true,
		legendRequired = true,
	} = additionals;

	const leftPaths = paths.filter((path) => path.axis === 'left');
	const rightPaths = paths.filter((path) => path.axis === 'right');
	const leftAxisMax = resolveAxisMax(data, leftPaths);
	const rightAxisMax = resolveAxisMax(data, rightPaths);
	const resolvedMargin = {
		top: Math.max(margin.top, 80),
		right: 140,
		bottom: Math.max(margin.bottom, 30),
		left: 110,
	};

	const xMax = width - resolvedMargin.left - resolvedMargin.right;
	const yMax = height - resolvedMargin.top - resolvedMargin.bottom;

	const xAxis = scaleBand<string>({
		domain: data.map(xAccessor),
		padding: 0.4,
	});

	const leftAxis = scaleLinear<number>({
		domain: [0, leftAxisMax],
		nice: true,
	});

	const rightAxis = scaleLinear<number>({
		domain: [0, rightAxisMax],
		nice: true,
	});

	xAxis.rangeRound([0, xMax]);
	leftAxis.range([yMax, 0]);
	rightAxis.range([yMax, 0]);

	const labelScale = scaleOrdinal<string, string>({
		domain: paths.map((path) => path.key),
		range: paths.map((path) => path.color),
	});

	const xCoordinates: Record<string, number> = {};
	const datumByXAxisValue: Record<string, IObjProps> = {};

	data.forEach((datum) => {
		const axisValue = xAccessor(datum);

		xCoordinates[axisValue] = (xAxis(axisValue) || 0) + xAxis.bandwidth() / 2;
		datumByXAxisValue[axisValue] = datum;
	});

	let tooltipTimeout: number;
	const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<{
		label: string;
		values: Array<{ color: string; label: string; value: number }>;
	}>();

	const getXAxisValueFromPoint = (xValue: number) => {
		const entries = Object.entries(xCoordinates);

		if (!entries.length) {
			return null;
		}

		return entries.reduce((closestEntry, currentEntry) =>
			Math.abs(currentEntry[1] - xValue) < Math.abs(closestEntry[1] - xValue) ? currentEntry : closestEntry
		)[0];
	};

	const getYScale = (path: IDualAxisLinePathProps) => (path.axis === 'left' ? leftAxis : rightAxis);

	const renderSideAxisLabel = (label: string, position: 'left' | 'right', color?: string) => (
		<div
			className={css({
				position: 'absolute',
				top: toRem(resolvedMargin.top + yMax / 2),
				[position]: position === 'left' ? '1rem' : '1.25rem',
				transform: position === 'left' ? 'translateY(-50%) rotate(-90deg)' : 'translateY(-50%) rotate(90deg)',
				transformOrigin: 'center',
				display: 'flex',
				alignItems: 'center',
				gap: '0.35rem',
				color: theme.colors.primaryA,
				fontSize: '0.75rem',
				fontWeight: 500,
				pointerEvents: 'none',
			})}
		>
			<span
				className={css({
					width: '0.75rem',
					height: '0.75rem',
					backgroundColor: color || theme.colors.primaryA,
					borderTopLeftRadius: '0.2rem',
					borderTopRightRadius: '0.2rem',
					borderBottomLeftRadius: '0.2rem',
					borderBottomRightRadius: '0.2rem',
					flexShrink: 0,
				})}
			/>
			<span>{label}</span>
		</div>
	);

	return (
		<div style={{ position: 'relative' }}>
			<div
				style={{
					position: 'absolute',
					fontSize: '0.875rem',
					display: 'flex',
					width: '100%',
					marginTop: '1rem',
					justifyContent: 'space-between',
				}}
			>
				{description && (
					<LabelSmall
						color={theme.colors.primaryA}
						overrides={{
							Block: {
								style: {
									fontWeight: 700,
									marginLeft: toRem(resolvedMargin.left),
								},
							},
						}}
					>
						{description}
					</LabelSmall>
				)}
				{legendRequired && (
					<LegendOrdinal
						scale={labelScale}
						direction='column'
						itemMargin={2}
						labelAlign='flex-start'
						labelMargin='0 1.875rem 0 0'
						className={css({
							color: theme.colors.primaryA,
							fontSize: '0.75rem',
							fontWeight: 500,
							lineHeight: '1rem',
							overflow: 'hidden',
							marginLeft: description ? '' : '1rem',
						})}
						shapeStyle={() => ({
							width: '0.75rem',
							height: '0.75rem',
							marginBottom: '0.125rem',
							...borderRadius('0.25rem'),
						})}
						labelFormat={(label) => paths.find((path) => path.key === label)?.labelFormat || label}
					/>
				)}
			</div>

			{leftYAxisLabel && renderSideAxisLabel(leftYAxisLabel, 'left', leftPaths[0]?.color)}
			{rightYAxisLabel &&
				!!rightPaths.length &&
				renderSideAxisLabel(rightYAxisLabel, 'right', rightPaths[0]?.color)}

			<svg width={width} height={height}>
				<rect x={0} y={0} width={width} height={height} fill={background} rx={radius} />
				<Group left={resolvedMargin.left} top={resolvedMargin.top}>
					<GridRows scale={leftAxis} width={xMax} numTicks={leftNumTicks} stroke={gridStroke} opacity={0.2} />
					<GridColumns scale={xAxis} height={yMax} stroke={gridStroke} opacity={0.2} />
					<AxisLeft
						scale={leftAxis}
						hideTicks
						numTicks={leftNumTicks}
						tickFormat={leftYFormatter}
						stroke={theme.colors.accent100}
						tickLabelProps={() => ({
							fontSize: '0.625rem',
							textAnchor: 'end',
							fill: theme.colors.primaryA,
						})}
					/>
					{!!rightPaths.length && (
						<AxisRight
							left={xMax}
							scale={rightAxis}
							hideTicks
							numTicks={rightNumTicks}
							tickFormat={rightYFormatter}
							stroke={theme.colors.accent100}
							tickLabelProps={() => ({
								fontSize: '0.625rem',
								textAnchor: 'start',
								fill: theme.colors.primaryA,
							})}
						/>
					)}
					<AxisBottom
						top={yMax}
						scale={xAxis}
						left={axisBottomLeft}
						hideTicks
						stroke={gridStroke}
						tickFormat={xFormatter}
						tickLabelProps={() => ({
							fontSize: '0.625rem',
							textAnchor: 'middle',
							fill: theme.colors.primaryA,
							dx: isSmallDesktopScreen ? '0rem' : '-1rem',
						})}
					/>
					{paths.map((path) => (
						<g key={path.key}>
							<LinePath
								data={data}
								curve={curveMonotoneX}
								x={(datum) => xCoordinates[xAccessor(datum)] || 0}
								y={(datum) => getYScale(path)(path.yAccessor(datum)) || 0}
								stroke={path.color}
								strokeWidth={path.stroke}
							/>
							{data.map((datum) => (
								<circle
									key={`${path.key}-${xAccessor(datum)}`}
									cx={xCoordinates[xAccessor(datum)] || 0}
									cy={getYScale(path)(path.yAccessor(datum)) || 0}
									r={4}
									fill={path.color}
									stroke={theme.colors.primaryB}
									strokeWidth={1.5}
								/>
							))}
						</g>
					))}
					<rect
						x={0}
						y={0}
						width={xMax}
						height={yMax}
						fill='transparent'
						onMouseMove={(event) => {
							if (!tooltipRequired) {
								return;
							}

							if (tooltipTimeout) {
								clearTimeout(tooltipTimeout);
							}

							const point = localPoint(event);
							const chartX = point?.x ? point.x - margin.left : 0;
							const xAxisValue = getXAxisValueFromPoint(chartX);

							if (!xAxisValue) {
								return;
							}

							const datum = datumByXAxisValue[xAxisValue];

							showTooltip({
								tooltipLeft: xCoordinates[xAxisValue] + margin.left,
								tooltipTop: point?.y,
								tooltipData: {
									label: xAxisValue,
									values: paths.map((path) => ({
										color: path.color,
										label: path.labelFormat || path.key,
										value: path.yAccessor(datum) || 0,
									})),
								},
							});
						}}
						onMouseLeave={() => {
							if (!tooltipRequired) {
								return;
							}

							tooltipTimeout = window.setTimeout(() => hideTooltip(), 200);
						}}
					/>
				</Group>
			</svg>
			{tooltipOpen && tooltipData && tooltipLeft && tooltipTop && (
				<div
					className={css({
						position: 'absolute',
						top: toRem(tooltipTop - 8),
						left: toRem(tooltipLeft > 300 ? tooltipLeft - 140 : tooltipLeft + 16),
						pointerEvents: 'none',
						backgroundColor: theme.colors.primaryA,
						borderTopLeftRadius: '0.5rem',
						borderTopRightRadius: '0.5rem',
						borderBottomLeftRadius: '0.5rem',
						borderBottomRightRadius: '0.5rem',
						paddingTop: '0.5rem',
						paddingRight: '0.65rem',
						paddingBottom: '0.5rem',
						paddingLeft: '0.65rem',
						minWidth: '8rem',
					})}
				>
					<div
						className={css({
							color: theme.colors.primaryB,
							fontSize: '0.675rem',
							fontWeight: 500,
							marginBottom: '0.35rem',
						})}
					>
						{tooltipData.label}
					</div>
					{tooltipData.values.map((value) => (
						<div
							key={value.label}
							className={css({
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '0.5rem',
								color: theme.colors.primaryB,
								fontSize: '0.75rem',
								lineHeight: '1.1rem',
							})}
						>
							<div
								className={css({
									display: 'flex',
									alignItems: 'center',
									gap: '0.35rem',
								})}
							>
								<span
									className={css({
										width: '0.6rem',
										height: '0.6rem',
										backgroundColor: value.color,
										borderTopLeftRadius: '0.25rem',
										borderTopRightRadius: '0.25rem',
										borderBottomLeftRadius: '0.25rem',
										borderBottomRightRadius: '0.25rem',
										flexShrink: 0,
									})}
								/>
								<span>{value.label}</span>
							</div>
							<span
								className={css({
									fontWeight: 700,
								})}
							>
								{value.value}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default ENGTDualAxisLineChart;
